import React, { useEffect } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import styles from './viewClient.module.css';
import { useHasPermission } from '../../../contexts/auth';
import { useBreadcrumbs } from '../../../components/ui/breadcrumps/breadcrumbsContext';
import { useCRUD } from '../../../api/crud';
import { CLIENTS_ENDPOINT } from '../../../consts';

export const ViewClient = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { setOverride } = useBreadcrumbs();
  const { get } = useCRUD(CLIENTS_ENDPOINT);
  const canViewContracts = useHasPermission("settlement.view_contract");
  const canViewSettlements = useHasPermission("settlement.view_settlement");
  const canViewEquipments = useHasPermission("settlement.view_equipment");

  // Fetch client data for breadcrumb override
  useEffect(() => {
    const fetchClient = async () => {
      if (id) {
        const response = await get(id);
        setOverride(`/clients/${id}`, response.data ? (response.data.short_name || response.data.name) : "");
      }
    };
    fetchClient();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, get]);

  // Extract client ID from pathname
  const pathname = location.pathname;
  const clientIdMatch = pathname.match(/\/clients\/([^/]+)/);
  const clientId = clientIdMatch ? clientIdMatch[1] : null;
  const basePath = clientId ? `/clients/${clientId}` : '/clients';

  const visibleTabs = [
    { key: 'basic', path: basePath },
    ...(canViewContracts ? [{ key: 'contracts', path: `${basePath}/contracts` }] : []),
    ...(canViewSettlements ? [{ key: 'settlements', path: `${basePath}/settlements` }] : []),
    ...(canViewEquipments ? [
      { key: 'active-equipment', path: `${basePath}/active-equipment` },
      { key: 'expired-equipment', path: `${basePath}/expired-equipment` }
    ] : []),
  ];

  const getTabValue = () => {
    if (pathname.includes('/settlements')) {
      const index = visibleTabs.findIndex(tab => tab.key === 'settlements');
      return index >= 0 ? index : 0;
    }
    if (pathname.includes('/expired-equipment')) {
      const index = visibleTabs.findIndex(tab => tab.key === 'expired-equipment');
      return index >= 0 ? index : 0;
    }
    if (pathname.includes('/active-equipment')) {
      const index = visibleTabs.findIndex(tab => tab.key === 'active-equipment');
      return index >= 0 ? index : 0;
    }
    if (pathname.includes('/contracts') && !pathname.endsWith('/contracts')) {
      const index = visibleTabs.findIndex(tab => tab.key === 'contracts');
      return index >= 0 ? index : 0;
    }
    if (pathname.includes('/contracts')) {
      const index = visibleTabs.findIndex(tab => tab.key === 'contracts');
      return index >= 0 ? index : 0;
    }
    return 0;
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    const selectedTab = visibleTabs[newValue];
    if (selectedTab) {
      navigate(selectedTab.path);
    } else {
      navigate(basePath);
    }
  };

  return (
    <Box className={styles.container}>
      <Tabs
        value={getTabValue()}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
      >
        {visibleTabs.map((tab) => {
          const labels: Record<string, string> = {
            basic: t("Basic Information"),
            contracts: t("Contracts"),
            settlements: t("Settlements"),
            "active-equipment": t("Active equipment"),
            "expired-equipment": t("Previously used equipment"),
          };
          return <Tab key={tab.key} label={labels[tab.key]} />;
        })}
      </Tabs>
      <Box className={styles.content}>
        <Outlet />
      </Box>
    </Box>
  );
};
